import React, { useEffect, useState } from 'react';
import TextField from '@shared/ui/atoms/TextField';
import TeamDropDownList from '@shared/ui/organisms/team-drop-down-list/TeamDropDownList';
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectAllTeams } from "@shared/store/config.duck";
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { addNewUser, selectError, selectErrorCode, selectIsLoading, selectSelectedTeam } from "../ducks/add-users.duck";
import { ErrorCode } from '@shared/utils/error-helper';
import { Link } from "react-router-dom";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { selectNumberOfRemainingUsers } from '@features/settings/store/payments.duck';
import { isInTeams } from '@shared/utils/TeamsUtils';
import { BodyError, BodyLarge } from '@shared/ui/atoms/fonts/Body';
import { Trans, useTranslation } from "react-i18next";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Colours from '@shared/ui/atoms/Colours';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function AddNewUserForm(props: Props) {
  const dispatch = useDispatch()
  const teams = useSelector(selectAllTeams);
  const isLoading = useSelector(selectIsLoading);
  const remainingSpaces = useSelector(selectNumberOfRemainingUsers);
  const error = useSelector(selectError);
  const errorCode = useSelector(selectErrorCode);
  const {t} = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [teamId, setTeamId] = useState('');
  const [officeId, setOfficeId] = useState(0);
  const selectedTeam = useSelector(selectSelectedTeam);
  const offices = useSelector(selectAllOfficeEntities);

  // Preselect the team when the form is opened for a specific one
  // (e.g. from the edit-team page's add-member dialog).
  useEffect(() => {
    if (selectedTeam) {
      setTeamId(selectedTeam.id);
    }
  }, [selectedTeam]);

  const addNewMember = () => {
    if (isLoading) {
      return;
    }
    setFirstNameError('')
    setLastNameError('')
    setEmailError('')
    if (!firstName) {
      setFirstNameError('Required');
    }
    if (!lastName) {
      setLastNameError('Required');
    }
    if (!email) {
      setEmailError('Required');
    }
    if (!!firstNameError || !!lastNameError || !!emailError) {
      return;
    }

    dispatch(addNewUser({
      firstname: firstName,
      lastname: lastName,
      emailAddress: email,
      teamId: teamId,
      defaultLocationId: officeId > 0 ? officeId : undefined,
    }))
  }

  // Known backend errors get a translated message; anything else shows the raw server text.
  const renderError = () => {
    switch (errorCode) {
      case ErrorCode.UserDeleted:
        return <Trans i18nKey={'manage.add-new-user.user-deleted'}
                      components={[<Link to={'/manage/deleted-users'} style={{color: 'inherit', textDecoration: 'underline'}} />]} />;
      case ErrorCode.UserAlreadyInAccount:
        return <Trans i18nKey={'manage.add-new-user.user-already-in-account'}
                      components={[<Link to={'/manage/users?view=user'} style={{color: 'inherit', textDecoration: 'underline'}} />]} />;
      default:
        return error;
    }
  }

  if (remainingSpaces === 0) {
    return <>
      {isInTeams() ? <>
        <BodyLarge style={{marginBottom: 24}}>{t('settings.account-full-in-teams')}</BodyLarge>
      </> : <>
        <BodyLarge style={{marginBottom: 24}}><Trans i18nKey={'settings.account-full-normal'} /></BodyLarge>

        <div style={{marginBottom: 16}}>
          <OutlineButton dataTest="upgradeButton"
                         size={ButtonSize.Large}
                         text="button.upgrade-my-account"
                         link={"/manage/payments"}
                         fullWidth={true}/>
        </div>
      </>}
    </>
  }

  return (
    <InputList onSubmit={(e) => e.preventDefault()} autoComplete="off">
      <input hidden={true} style={{display: 'none'}} autoComplete="false" />
      {error && <BodyError style={{whiteSpace: 'pre-line'}}>{renderError()}</BodyError>}
      <TextField fullWidth={true}
                 disabled={isLoading}
                 dataTest="firstNameInput"
                 label="textinput.firstname"
                 error={firstNameError}
                 value={firstName}
                 onChange={(val: string) => setFirstName(val)}/>
      <TextField fullWidth={true}
                 disabled={isLoading}
                 dataTest="lastNameInput"
                 label="textinput.lastname"
                 error={lastNameError}
                 value={lastName}
                 onChange={(val: string) => setLastName(val)}/>

      <TeamDropDownList teams={teams}
                        disabled={isLoading}
                        selectedTeamId={teamId}
                        label={"Select a team"}
                        onSelect={(teamId) => setTeamId(teamId)} />
      {offices.length > 0 &&
        <OfficeDropDownList label="textinput.office-location"
                            disabled={isLoading}
                            offices={offices}
                            isClearable={true}
                            portalMenu={true}
                            className="teamDropDownList"
                            onSelect={(office: OfficeEntity | null) => setOfficeId(office?.id ?? 0)} />}
      <TextField fullWidth={true}
                 disabled={isLoading}
                 dataTest="emailInput"
                 label="login.email-address"
                 value={email}
                 error={emailError}
                 onChange={(val: string) => setEmail(val)}/>

      <PrimaryButton fullWidth={true}
                     size={ButtonSize.Large}
                     dataTest="submitAddMember"
                     buttonType="submit"
                     text="button.send-invite"
                     disabled={isLoading}
                     click={addNewMember}/>
    </InputList>
  )
}

interface Props {
}

const InputList = styled.form`
  .teamDropDownList__label, .switch__label, .textField__label {
    color: ${Colours.black} !important;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }
`
